





























































import Enumerable from "linq";
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { Size, Color } from "../../../../../../base/domain/drawing";
import { SizeEditor } from "../../../../../../base/components/drawing";
import { ModelEditableMixin } from "../../../../../../base/components/system/mixins";
import { OptionRadioSelector } from "../../../../../../base/components/system/selectors";
import { StandardOption } from "../../../../../../base/components/system/selectors/mixins";
import { NewObjectKeys, IConfiguration } from "../../../../../domain/configuration";
import { FormatPageStructureEditor, IFormatObjectEditable } from "../../../../../domain/editableFormat";
import { ScriptObjectEditor, GraphObjectEditor } from "../../../../../domain/editableFormat/objectEditors";
import { FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin } from "../../mixins";
import {
  LocatorKeys,
  LocatorTypes,
  ILocatorDefinition,
  VectorObjectLocator,
  RectangleObjectLocator,
  RectangleObjectLocators,
  PositionCorrectionObjectLocator,
} from "./locators";

enum Methods {
  Drag,
  Stamp,
}

enum Stamps {
  Small,
  Medium,
  Large,
  Custom,
}

interface ILocatorBehavior {
  isLocatableContinuously: boolean;
  isStampable: boolean;
}

interface ILocator {
  key: LocatorKeys;
  type: LocatorTypes;
  behavior: ILocatorBehavior;
  option?: NewObjectKeys | ({ key: NewObjectKeys, name: string, description: string }[]);
}

@Component({
  components: {
    OptionRadioSelector,
    SizeEditor,
    VectorObjectLocator,
    RectangleObjectLocator,
    RectangleObjectLocators,
    PositionCorrectionObjectLocator,
  },
})
export default class FormatObjectLocateTool extends Mixins(ModelEditableMixin, FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin) {
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  @Prop()
  pallet: Color[];

  @Prop()
  locatorKey: LocatorKeys;

  @Prop()
  validLocators: { [key in LocatorKeys]: ILocatorDefinition };

  @Prop()
  isShown: boolean;

  @Emit()
  located(shouldLocateContinuously: boolean): void {}

  data() {
    return {
      shouldLocateContinuously: false,
      selectedMethod: Methods.Drag,
      selectedStamp: Stamps.Small,
      selectedStampSize: new Size(20, 20),
    };
  }
  shouldLocateContinuously: boolean;
  selectedMethod: Methods;
  selectedStamp: Stamps;
  selectedStampSize: Size;

  $configuration!: IConfiguration;

  get methods(): StandardOption<Methods>[] {
    return [
      new StandardOption("ドラッグ", Methods.Drag, "ドラッグで位置サイズを指定して配置します。"),
      new StandardOption("スタンプ", Methods.Stamp, "クリックした位置に指定サイズで配置します。"),
    ];
  }

  get shouldStamp(): boolean {
    return (this.selectedMethod === Methods.Stamp);
  }

  get stamps(): StandardOption<Stamps>[] {
    return [
      new StandardOption("大", Stamps.Large, "40x40サイズで配置します。"),
      new StandardOption("中", Stamps.Medium, "30x30サイズで配置します。"),
      new StandardOption("小", Stamps.Small, "20x20サイズで配置します。"),
      new StandardOption("指定", Stamps.Custom, "指定されたサイズで配置します。"),
    ];
  }

  get shouldStampCustomSize(): boolean {
    return this.shouldStamp
        && (this.selectedStamp === Stamps.Custom);
  }

  @Watch("selectedStamp")
  onSelectedStampChanged(newValue: Stamps): void {
    if (newValue === Stamps.Large) {
      this.selectedStampSize = new Size(40, 40);
    } else if (newValue === Stamps.Medium) {
      this.selectedStampSize = new Size(30, 30);
    } else if (newValue === Stamps.Small) {
      this.selectedStampSize = new Size(20, 20);
    }
  }

  get isLocatableContinuously(): boolean {
    return this.locator
        && this.locator.behavior
        && this.locator.behavior.isLocatableContinuously;
  }

  get isStampable(): boolean {
    return this.$configuration.formatEditor.behavior.format.editor.object.isStampable
        && this.locator
        && this.locator.behavior
        && this.locator.behavior.isStampable;
  }

  get locator(): ILocator {
    return Enumerable.from(this.locators)
      .firstOrDefault(l => l.key === this.locatorKey);
  }

  get locators(): ILocator[] {
    return Enumerable.from(Object.keys(this.validLocators))
      .cast<LocatorKeys>()
      .where(k => !!this.validLocators[k] && !!FormatObjectLocateTool._locatorBehaviors[this.validLocators[k].type])
      .select(k => ({
        key: k,
        type: this.validLocators[k].type,
        behavior: FormatObjectLocateTool._locatorBehaviors[this.validLocators[k].type],
        option: this.validLocators[k].option,
      }))
      .toArray();
  }

  get shouldToLocateObjects(): boolean {
    return this.locator
        && (this.locator.type === "ExternalData" || this.locator.type === "RangeOfMotion");
  }

  async toLocateObjects(source: IFormatObjectEditable): Promise<IFormatObjectEditable[]> {
    if (source instanceof ScriptObjectEditor) {
      return this.toNewExternalDataObjects(source);
    } else if (source instanceof GraphObjectEditor) {
      return this.toNewRangeOfMotionObjects(source);
    } else {
      return [];
    }
  }

  private async toNewExternalDataObjects(source: ScriptObjectEditor): Promise<IFormatObjectEditable[]> {
    const scriptGroupEditor = this.getCommonComponents().getScriptGroupEditor();
    const scriptGroupKey = source.scriptGroupKey().getValue();
    if (scriptGroupKey === "") {
      await scriptGroupEditor.alertEmptyKey();
      return [];
    }
    if (scriptGroupEditor.containsSameKey(scriptGroupKey)) {
      if (await scriptGroupEditor.confirmCreateUniqueKey()) {
        source.scriptGroupKey().setValue(scriptGroupEditor.createUniqueKey(scriptGroupKey));
      } else {
        return [];
      }
    }
    const tableSize = source.tableSize().getValue();
    const objects: IFormatObjectEditable[] = [];
    for (let r = 0; r < tableSize.row; r++) {
      for (let c = 0; c < tableSize.column; c++) {
        objects.push(scriptGroupEditor.createNewExternalDataObject(source, r, c));
      }
    }
    return objects;
  }

  private toNewRangeOfMotionObjects(source: GraphObjectEditor): IFormatObjectEditable[] {
    return this.getCommonComponents().getRangeOfMotionGroupEditor().createNewRangeOfMotionObjects(source);
  }

  private static readonly _locatorBehaviors: { [key in LocatorTypes]: ILocatorBehavior } = {
    Vector: {
      isLocatableContinuously: true,
      isStampable: false,
    },
    Rectangle: {
      isLocatableContinuously: true,
      isStampable: true,
    },
    Rectangles: {
      isLocatableContinuously: true,
      isStampable: true,
    },
    PositionCorrection: {
      isLocatableContinuously: false,
      isStampable: false,
    },
    ExternalData: {
      isLocatableContinuously: true,
      isStampable: true,
    },
    RangeOfMotion: {
      isLocatableContinuously: true,
      isStampable: true,
    },
    NotImplemented: null,
  };
}
